import {
    ChangeEvent,
    ClickEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, Event, LookupModelSearchEvent, Panel, SelectionMode, Switch, Tab, TableRow, TableRowCreationEvent,
    TableSelectionEvent
} from "@mcleod/components";
import { Api, LicenseTester, StringUtil, UrlUtil } from "@mcleod/core";
import { SalesCallListTable } from "./SalesCallListTable";
import { SalesCallListTableType } from "./SalesCallListTableType";
import { AutogenLayoutSalesCallList } from "./autogen/AutogenLayoutSalesCallList";
import { LMEUserSettings } from "@mcleod/general";
import { CommonDialogs } from "@mcleod/common";
import { SalesCallMultiSelectErrorModal } from "@mcleod/general/src/SalesCallMultiSelectErrorModal";

interface SalesCallListTab {
    type: SalesCallListTableType;
    layout?: SalesCallListTable;
    tab?: Tab;
}

export class SalesCallList extends AutogenLayoutSalesCallList {
    private urlParams: any = UrlUtil.getPropsFromUrl(window.location.href);

    private tabs: SalesCallListTab[] = [
        { type: SalesCallListTableType.ALL },
        { type: SalesCallListTableType.TODAY },
    ]

    override onLoad() {
        this.setupTables();
        this.defaultUser();
        this.mainDataSource.executeValidatedSearch().then(() => this.setSecondaryFilters());
        this.tableCallRecords.selectionMode = LicenseTester.testPackage("com.tms.client.loadmaster.crm") ? SelectionMode.MULTI : SelectionMode.NONE;
    }

    defaultStatus() {
        this.mainDataSource.searchRow.set("is_active", "Y");
        this.mainDataSource.displayDataInBoundSearchComponent(this.textboxStatusFilter);
    }

    defaultUser() {
        this.textboxAssignedTo.enabled = true;
        LMEUserSettings.setUserLookupModelData("assigned_csr", this.mainDataSource.searchRow);
        this.mainDataSource.displayDataInBoundSearchComponent(this.textboxAssignedTo);
    }

    buttonClearOnClick(event: ClickEvent) {
        this.mainDataSource.searchRow.set("all_customers", "Y");
        this.mainDataSource.searchRow.set("all_locations", "Y");
        this.mainDataSource.searchRow.set("salesperson", null);
        this.mainDataSource.searchRow.set("is_active", null);
        this.defaultUser();
        this.panelSearch.displayData(this.mainDataSource.searchRow, null, 0);
    }

    setupTables() {
        for (let i = 0; i < this.tabs.length; i++) {
            const table = this.tabsetMain.getTab(i).components[0];
            if (table instanceof SalesCallListTable) {
                table.configureTableForType(this.tabs[i].type);
                this.tabs[i].layout = table;
                this.tabs[i].tab = this.tabsetMain.getTab(i);
                table.salesCallList = this;
                table.tableSalesCalls.addRowCreateListener((event: TableRowCreationEvent) => event.getTable().busy = false);
            }
        }
    }

    private setSecondaryFilters() {
        if (this.urlParams?.["locationProspect"]) {
            this.layoutAll.findComponentById('btn' + SalesCallListTableType.LOCATION_PROSPECT.id).clicked();
            this.layoutToday.findComponentById('btn' + SalesCallListTableType.LOCATION_PROSPECT.id).clicked();
        }
        else if (this.urlParams?.["customer"]) {
            this.layoutAll.findComponentById('btn' + SalesCallListTableType.CUSTOMER.id).clicked();
            this.layoutToday.findComponentById('btn' + SalesCallListTableType.CUSTOMER.id).clicked();
        }
    }

    textboxAssignedToBeforeLookupModelSearch(event) {
        event.filter.web_user_type = "U";
    }

    switchSearchAllCSROnChange(event: Event) {
        const searchAll = event.target as any;
        this.textboxAssignedTo.enabled = !searchAll.checked;
        if (searchAll.checked) {
            this.textboxAssignedTo.text = "";
            this.mainDataSource.searchRow.set("assigned_csr", null);
        }
    }

    switchSearchAllCustomersOnChange(event: ChangeEvent) {
        const searchAll = event.target as Switch;
        this.textboxCustomer.enabled = !searchAll.checked;
        if (searchAll.checked) {
            this.mainDataSource.searchRow.set("customer", null);
            this.mainDataSource.displayDataInBoundSearchComponent(this.textboxCustomer);
        }
    }

    switchSearchAllLocationsOnChange(event: ChangeEvent) {
        const searchAll = event.target as Switch;
        this.textboxLocation.enabled = !searchAll.checked;
        if (searchAll.checked) {
            this.mainDataSource.searchRow.set("location", null);
            this.mainDataSource.displayDataInBoundSearchComponent(this.textboxLocation);
        }
    }

    updateTabTitle(tab: SalesCallListTab) {
        const modelRows = this.sourceSalesCalls.data.filter(tab.type.filter);
        tab.tab.caption = tab.type.caption + " " + modelRows.length;
    }

    buttonSearchOnClick(event: ClickEvent) {
        if (!this.switchSearchAllCSR.checked && this.textboxAssignedTo.isEmpty()) {
            this.defaultUser();
        }
        this.mainDataSource.executeValidatedSearch().then(() => this.populateFilteredTables());
    }

    sourceSalesCallsBeforeExecution(event: DataSourceExecutionEvent) {
        if (DataSourceAction.SEARCH == event.getAction()) {
            this.tabs.forEach(tab => tab.layout.tableSalesCalls.busy = true);
        }
    }

    sourceSalesCallsAfterExecution(event: DataSourceExecutionEvent) {
        if (DataSourceAction.SEARCH == event.getAction()) {
            this.tabs.forEach(tab => {
                tab.layout.tableSalesCalls.busy = false
                tab.layout.updateFilterCounts();
                this.updateTabTitle(tab);
            });
            this.populateFilteredTables();
            this.sourceSalesCallRecord.search(this.sourceSalesCalls.searchRow.data)
            .then(_=> this.tabHistory.caption = "History " + this.sourceSalesCallRecord.data.length); 
        }
    }

    textboxLocationBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    tableCallRecordsOnSelect(event: TableSelectionEvent) {
        const selectedRows = event.table.selectedRows ?? [];
        const hasValidRows = selectedRows.length > 0 && 
            selectedRows.every(row => {
                const type = row.data.get("type");
                const custlocId = row.data.get("custloc_id");
                const notTransferred = row.data.get("transferred") === "No";
                return (notTransferred && (type === "C" || custlocId != null));
            });
        
        this.buttonConvert.enabled = hasValidRows;
    }

    populateFilteredTables(tableType?: SalesCallListTableType) {
        this.tabs.forEach((tab, index) => {
            if (tableType != null && tableType != tab.type)
                return;

            this.populateFilteredTable(tab, index);
        })
    }

    populateFilteredTable(tab: SalesCallListTab, index: number) {
        const layout = tab.layout;
        const modelRows = this.sourceSalesCalls.data.filter(layout.filter);
        layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, modelRows, null);
        if (index != this.tabsetMain.selectedIndex && layout.tableSalesCalls.rowCount > 0) {
            // the following will populate up to 5 rows when the table is hidden.
            // This avoids populating them when the user switches tab
            const rowsToPopulate = Math.min(5, layout.tableSalesCalls.rowCount)
            for (let i = 0; i < rowsToPopulate; i++)
                layout.tableSalesCalls.rows[i]?.populateDOMIfNeeded();
        }
    }

    buttonConvertOnClick(event: ClickEvent) {
        const sales_calls = this.tableCallRecords.selectedRows.map(row => row.data);
        const value = "";
        CommonDialogs.showInputDialog(
                `Enter CRM Activity:`,
                "Convert to Customer Activity",
                {
                    required: true,
                    field: "activity_id_dialog",
                    lookupModel: "lme/crm/prospect-activity",
                    lookupModelDisplayField: "id",
                    lookupModelLayout: "lme/crm/ProspectActivityLookup",
                    dataSource: new DataSource({ id: "prospectDataSource", url: "lme/crm/prospect-action" })
                }, {
                    okCaption: "Convert",
                    panelTitleProps: { backgroundColor: "primary.darker", color: "primary.reverse" },
                    okProps: { backgroundColor: "primary", color: "primary.reverse", width: 110 },
                }
            ).then(async (reason) => {
                if (reason != null) {
                    const response = await Api.post("lme/crm/convert-market-call", { sales_calls, activity_id: reason });
                    const allRows = response.data;
                    const successCount = response.data.filter(result => result.success).length;
                    const failedRows = [];
                    const errorRows = [];
                    response.data.forEach(result => {
                        const row = this.tableCallRecords.selectedRows.find(row => row.data.get("order_id") === result.order_id);
                        if (!StringUtil.isEmptyString(result.error)) {
                            if (!result.success && result.retry) {
                                row.data.set("error", result.error);
                                failedRows.push(row);
                            } else {
                                const errorRow = row.data.createBasicCopy();
                                errorRow.set("error", result.error);
                                errorRows.push(errorRow);
                            }
                        }
                    });
                    SalesCallMultiSelectErrorModal.showModal(this.tableCallRecords.selectedRows, failedRows, successCount, "To Customer Activity");
                }
            });
    }
}
